import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  extractStageEntryDateMaps,
  getStageEntryDate as resolveStageEntryDate,
  type StageEntryDateMaps,
} from 'shared/domain/changelog/extractStageEntryDates';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { issueClient } from 'shared/domain/issue/issueClient';
import { useIssueStore } from '../store';
import type {
  UseIssueChangelogOptions,
  UseIssueChangelogResult,
  UseStageEntryDatesOptions,
  UseStageEntryDatesResult,
} from '../slices/issueChangelog/types';

const emptyDateMaps: StageEntryDateMaps = {
  firstEntryDates: new Map(),
  lastEntryDates: new Map(),
};

export function useIssueChangelog(
  issueId: string | undefined,
  options?: UseIssueChangelogOptions
): UseIssueChangelogResult {
  const enabled = options?.enabled ?? true;
  const requestIssueChangelog = useIssueStore(
    (state) => state.requestIssueChangelog
  );

  useLayoutEffect(() => {
    if (!issueId || !enabled) {
      return;
    }
    void requestIssueChangelog(issueId);
  }, [enabled, issueId, requestIssueChangelog]);

  const storeEntry = useIssueStore((state) =>
    issueId ? state.issueChangelogEntries[issueId] : undefined
  );

  return useMemo(
    (): UseIssueChangelogResult => ({
      mappedChangelog: storeEntry?.mapped ?? [],
      loading: storeEntry?.loading ?? false,
      ready: Boolean(
        issueId &&
          storeEntry &&
          storeEntry.fetched &&
          !storeEntry.loading &&
          storeEntry.error === null
      ),
      error: storeEntry?.error ?? null,
      refetch: async (): Promise<void> => {
        if (!issueId) {
          return;
        }
        await requestIssueChangelog(issueId, { force: true });
      },
    }),
    [issueId, requestIssueChangelog, storeEntry]
  );
}

/** Start changelog fetch as early as possible (e.g. when issue view mounts). */
export function usePrefetchIssueChangelog(issueId: string | undefined): void {
  const requestIssueChangelog = useIssueStore(
    (state) => state.requestIssueChangelog
  );

  useLayoutEffect(() => {
    if (!issueId) {
      return;
    }
    void requestIssueChangelog(issueId);
  }, [issueId, requestIssueChangelog]);
}

export function useStageEntryDates(
  issueId: string | undefined,
  stageId: string | undefined,
  issueCreatedAt: string | undefined,
  firstStageId: string | undefined,
  options?: UseStageEntryDatesOptions
): UseStageEntryDatesResult {
  const workflowStagesReady = options?.workflowStagesReady ?? true;
  const { mappedChangelog, loading, ready, error } = useIssueChangelog(issueId);
  const requestIssueChangelog = useIssueStore(
    (state) => state.requestIssueChangelog
  );

  const previousStageIdRef = useRef<string | undefined>(undefined);
  const previousIssueIdRef = useRef<string | undefined>(undefined);
  const lastReadyIssueIdRef = useRef<string | undefined>(undefined);
  const lastReadyDateMapsRef = useRef<StageEntryDateMaps>(emptyDateMaps);

  if (issueId !== previousIssueIdRef.current) {
    previousStageIdRef.current = undefined;
    lastReadyIssueIdRef.current = undefined;
    lastReadyDateMapsRef.current = emptyDateMaps;
    previousIssueIdRef.current = issueId;
  }

  const hasStaleMapsForCurrentIssue =
    lastReadyIssueIdRef.current === issueId &&
    lastReadyDateMapsRef.current.firstEntryDates.size > 0;

  const awaitingWorkflowStages =
    !workflowStagesReady || (Boolean(issueId) && !firstStageId);

  useEffect(() => {
    if (!issueId) {
      return;
    }

    return issueClient.onUpdated((event) => {
      if (event.data && areObjectIdsEqual(event.data._id, issueId)) {
        void requestIssueChangelog(issueId, { force: true });
      }
    });
  }, [issueId, requestIssueChangelog]);

  useEffect(() => {
    if (!issueId || !stageId) {
      return;
    }
    if (
      previousStageIdRef.current !== undefined &&
      previousStageIdRef.current !== stageId
    ) {
      void requestIssueChangelog(issueId, { force: true });
    }
    previousStageIdRef.current = stageId;
  }, [issueId, requestIssueChangelog, stageId]);

  const dateMaps = useMemo((): StageEntryDateMaps => {
    if (error) {
      return emptyDateMaps;
    }

    const canUseStaleMaps =
      Boolean(issueId) &&
      !ready &&
      loading &&
      hasStaleMapsForCurrentIssue;

    if (canUseStaleMaps) {
      return lastReadyDateMapsRef.current;
    }

    if (!firstStageId) {
      return emptyDateMaps;
    }

    const nextDateMaps = extractStageEntryDateMaps(mappedChangelog, {
      issueCreatedAt,
      firstStageId,
    });
    if (ready && issueId && workflowStagesReady && firstStageId) {
      lastReadyDateMapsRef.current = nextDateMaps;
      lastReadyIssueIdRef.current = issueId;
    }
    return nextDateMaps;
  }, [
    error,
    firstStageId,
    hasStaleMapsForCurrentIssue,
    issueCreatedAt,
    issueId,
    loading,
    mappedChangelog,
    ready,
    workflowStagesReady,
  ]);

  const getStageEntryDate = useCallback(
    (targetStageId: string, isCurrentStage: boolean): string | undefined =>
      resolveStageEntryDate(dateMaps, targetStageId, isCurrentStage),
    [dateMaps]
  );

  const datesLoading =
    Boolean(issueId) &&
    !error &&
    !hasStaleMapsForCurrentIssue &&
    !awaitingWorkflowStages &&
    !ready &&
    loading;

  const datesPending = datesLoading;

  return { getStageEntryDate, datesPending, datesLoading, loading, ready };
}
